package com.tovtalk.ui.screen.verification

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.CheckCircle
import androidx.compose.material.icons.filled.Error
import androidx.compose.material.icons.filled.Phone
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import com.tovtalk.di.koinInject
import com.tovtalk.domain.entity.DomainResult
import com.tovtalk.domain.repository.UserRepository
import com.tovtalk.ui.components.CenteredContentWrapper
import com.tovtalk.ui.scaffold.ScreenScaffold
import com.tovtalk.ui.scaffold.TovTalkTopBar
import com.tovtalk.ui.theme.TovTalkTheme
import com.tovtalk.utils.strings.replaceDays
import com.tovtalk.utils.strings.StringProvider
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch

@Composable
fun PhoneVerificationScreen(
    onNavigateBack: () -> Unit,
    userRepository: UserRepository = koinInject()
) {
    val scope = rememberCoroutineScope()
    
    var phoneNumber by remember { mutableStateOf("") }
    var verificationStatus by remember { mutableStateOf<String?>(null) }
    var verificationCode by remember { mutableStateOf<String?>(null) }
    var phoneToCall by remember { mutableStateOf<String?>(null) }
    var isLoading by remember { mutableStateOf(false) }
    var errorMessage by remember { mutableStateOf<String?>(null) }
    var isPolling by remember { mutableStateOf(false) }
    var statusLoaded by remember { mutableStateOf(false) }
    var canStart by remember { mutableStateOf(true) }
    var retryDaysRemaining by remember { mutableStateOf<Int?>(null) }
    
    // Check current status on load – show correct screen before user types phone
    LaunchedEffect(Unit) {
        when (val result = userRepository.getVerificationStatus()) {
            is DomainResult.Success -> {
                val d = result.data
                statusLoaded = true
                canStart = d.canStart
                retryDaysRemaining = d.retryDaysRemaining
                val status = d.status
                when (status) {
                    "pending" -> {
                        verificationStatus = status
                        verificationCode = d.code
                        phoneToCall = d.phoneToCall
                        isPolling = true
                    }
                    "verified", "approved" -> verificationStatus = "approved"
                    "rejected", "expired" -> verificationStatus = status
                    else -> {
                        // No request or null – allow new request
                        verificationStatus = null
                    }
                }
            }
            else -> {
                statusLoaded = true
                canStart = true
                retryDaysRemaining = null
            }
        }
    }

    fun startVerification() {
        // Validate phone number
        val cleanPhone = phoneNumber.trim()
        if (cleanPhone.isBlank()) {
            errorMessage = "נא להזין מספר טלפון"
            return
        }
        
        isLoading = true
        errorMessage = null
        scope.launch {
            when (val result = userRepository.startPhoneVerification(cleanPhone)) {
                is DomainResult.Success -> {
                    verificationStatus = "pending"
                    verificationCode = result.data.code
                    phoneToCall = result.data.phoneToCall
                    isLoading = false
                    isPolling = true
                }
                is DomainResult.Error -> {
                    errorMessage = result.message
                    isLoading = false
                }
                is DomainResult.Loading -> {
                    // Already handled by isLoading state
                }
            }
        }
    }

    fun checkStatus() {
        scope.launch {
            when (val result = userRepository.getVerificationStatus()) {
                is DomainResult.Success -> {
                    val s = result.data.status
                    verificationStatus = when (s) {
                        "verified" -> "approved"
                        else -> s
                    }
                    if (verificationStatus == "approved" || verificationStatus == "rejected" || verificationStatus == "expired") {
                        isPolling = false
                    }
                }
                is DomainResult.Error -> { /* Silently fail */ }
                is DomainResult.Loading -> { }
            }
        }
    }

    // Poll for status updates
    LaunchedEffect(isPolling) {
        while (isPolling) {
            delay(3000) // Check every 3 seconds
            checkStatus()
        }
    }

    ScreenScaffold(
        topBar = {
            TovTalkTopBar(
                title = StringProvider.get("verification_title"),
                onBack = onNavigateBack,
            )
        },
        constrainContentWidth = false,
    ) { paddingValues ->
        val phoneVerificationContent = @Composable {
        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(paddingValues)
                .verticalScroll(rememberScrollState())
                .padding(TovTalkTheme.spacing.md),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            Spacer(modifier = Modifier.height(TovTalkTheme.spacing.lg))

            // Icon
            Box(
                modifier = Modifier
                    .size(80.dp)
                    .clip(TovTalkTheme.shapes.large)
                    .background(TovTalkTheme.colors.brand.copy(alpha = TovTalkTheme.opacity.soft)),
                contentAlignment = Alignment.Center
            ) {
                Icon(
                    imageVector = Icons.Default.Phone,
                    contentDescription = null,
                    tint = TovTalkTheme.colors.brand,
                    modifier = Modifier.size(40.dp)
                )
            }

            Spacer(modifier = Modifier.height(TovTalkTheme.spacing.lg))

            // Title
            Text(
                text = StringProvider.get("verification_title"),
                style = TovTalkTheme.typography.headlineSmall,
                fontWeight = FontWeight.Bold,
                color = TovTalkTheme.colors.textPrimary
            )

            Spacer(modifier = Modifier.height(TovTalkTheme.spacing.sm))

            // Description
            Text(
                text = StringProvider.get("verification_description"),
                style = TovTalkTheme.typography.bodyMedium,
                color = TovTalkTheme.colors.textSecondary,
                textAlign = TextAlign.Center
            )

            Spacer(modifier = Modifier.height(TovTalkTheme.spacing.xl))

            if (!statusLoaded) {
                CircularProgressIndicator(
                    modifier = Modifier.size(40.dp),
                    color = TovTalkTheme.colors.brand
                )
                Spacer(modifier = Modifier.height(TovTalkTheme.spacing.md))
                Text(
                    text = StringProvider.get("verification_loading_status"),
                    style = TovTalkTheme.typography.bodyMedium,
                    color = TovTalkTheme.colors.textSecondary
                )
            } else if (!canStart && (retryDaysRemaining ?: 0) > 0) {
                // Cannot start – must wait (14-day rule)
                val days = retryDaysRemaining ?: 0
                Card(
                    modifier = Modifier.fillMaxWidth(),
                    colors = CardDefaults.cardColors(containerColor = TovTalkTheme.colors.surfaceElevated),
                    shape = TovTalkTheme.shapes.medium
                ) {
                    Column(
                        modifier = Modifier.padding(TovTalkTheme.spacing.md),
                        horizontalAlignment = Alignment.CenterHorizontally
                    ) {
                        Icon(
                            imageVector = Icons.Default.Error,
                            contentDescription = null,
                            tint = TovTalkTheme.colors.warning,
                            modifier = Modifier.size(48.dp)
                        )
                        Spacer(modifier = Modifier.height(TovTalkTheme.spacing.md))
                        Text(
                            text = StringProvider.get("verification_retry_after_days").replaceDays(days),
                            style = TovTalkTheme.typography.titleMedium,
                            fontWeight = FontWeight.Bold,
                            color = TovTalkTheme.colors.textPrimary,
                            textAlign = TextAlign.Center
                        )
                        Spacer(modifier = Modifier.height(TovTalkTheme.spacing.sm))
                        Text(
                            text = StringProvider.get("verification_retry_days_remaining").replaceDays(days),
                            style = TovTalkTheme.typography.bodyMedium,
                            color = TovTalkTheme.colors.textSecondary,
                            textAlign = TextAlign.Center
                        )
                    }
                }
            } else when (verificationStatus) {
                null -> {
                    // Step 1: Enter phone number only when canStart; otherwise show retry message
                    if (!canStart && retryDaysRemaining != null) {
                        val days = retryDaysRemaining ?: 0
                        Card(
                            modifier = Modifier.fillMaxWidth(),
                            colors = CardDefaults.cardColors(containerColor = TovTalkTheme.colors.surfaceElevated),
                            shape = TovTalkTheme.shapes.medium
                        ) {
                            Column(
                                modifier = Modifier.padding(TovTalkTheme.spacing.md),
                                horizontalAlignment = Alignment.CenterHorizontally
                            ) {
                                Icon(
                                    imageVector = Icons.Default.Error,
                                    contentDescription = null,
                                    tint = TovTalkTheme.colors.warning,
                                    modifier = Modifier.size(48.dp)
                                )
                                Spacer(modifier = Modifier.height(TovTalkTheme.spacing.md))
                                Text(
                                    text = StringProvider.get("verification_retry_after_days").replaceDays(days),
                                    style = TovTalkTheme.typography.titleMedium,
                                    fontWeight = FontWeight.Bold,
                                    color = TovTalkTheme.colors.textPrimary,
                                    textAlign = TextAlign.Center
                                )
                                Spacer(modifier = Modifier.height(TovTalkTheme.spacing.sm))
                                Text(
                                    text = StringProvider.get("verification_retry_days_remaining").replaceDays(days),
                                    style = TovTalkTheme.typography.bodyMedium,
                                    color = TovTalkTheme.colors.textSecondary,
                                    textAlign = TextAlign.Center
                                )
                            }
                        }
                    } else {
                    Card(
                        modifier = Modifier.fillMaxWidth(),
                        colors = CardDefaults.cardColors(containerColor = TovTalkTheme.colors.surfaceElevated),
                        shape = TovTalkTheme.shapes.medium
                    ) {
                        Column(
                            modifier = Modifier.padding(TovTalkTheme.spacing.md)
                        ) {
                            Text(
                                text = StringProvider.get("verification_step_1"),
                                style = TovTalkTheme.typography.titleMedium,
                                fontWeight = FontWeight.Bold,
                                color = TovTalkTheme.colors.textPrimary
                            )

                            Spacer(modifier = Modifier.height(TovTalkTheme.spacing.md))

                            OutlinedTextField(
                                value = phoneNumber,
                                onValueChange = { phoneNumber = it },
                                label = { Text(StringProvider.get("verification_phone_label")) },
                                placeholder = { Text(StringProvider.get("verification_phone_placeholder")) },
                                keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Phone),
                                modifier = Modifier.fillMaxWidth(),
                                colors = OutlinedTextFieldDefaults.colors(
                                    focusedBorderColor = TovTalkTheme.colors.brand,
                                    unfocusedBorderColor = TovTalkTheme.colors.border
                                ),
                                singleLine = true
                            )

                            if (errorMessage != null) {
                                Spacer(modifier = Modifier.height(TovTalkTheme.spacing.sm))
                                Text(
                                    text = errorMessage ?: "",
                                    style = TovTalkTheme.typography.bodySmall,
                                    color = TovTalkTheme.colors.error
                                )
                            }

                            Spacer(modifier = Modifier.height(TovTalkTheme.spacing.md))

                            Button(
                                onClick = { startVerification() },
                                modifier = Modifier.fillMaxWidth(),
                                enabled = !isLoading,
                                colors = ButtonDefaults.buttonColors(
                                    containerColor = TovTalkTheme.colors.brand
                                )
                            ) {
                                if (isLoading) {
                                    CircularProgressIndicator(
                                        modifier = Modifier.size(20.dp),
                                        color = TovTalkTheme.colors.onBrand,
                                        strokeWidth = 2.dp
                                    )
                                } else {
                                    Text(StringProvider.get("verification_start"))
                                }
                            }
                        }
                    }
                    }
                }
                "pending" -> {
                    // Step 2: Call the number
                    Card(
                        modifier = Modifier.fillMaxWidth(),
                        colors = CardDefaults.cardColors(containerColor = TovTalkTheme.colors.surfaceElevated),
                        shape = TovTalkTheme.shapes.medium
                    ) {
                        Column(
                            modifier = Modifier.padding(TovTalkTheme.spacing.md),
                            horizontalAlignment = Alignment.CenterHorizontally
                        ) {
                            Text(
                                text = StringProvider.get("verification_step_2"),
                                style = TovTalkTheme.typography.titleMedium,
                                fontWeight = FontWeight.Bold,
                                color = TovTalkTheme.colors.textPrimary
                            )

                            Spacer(modifier = Modifier.height(TovTalkTheme.spacing.md))

                            Text(
                                text = StringProvider.get("verification_call_instructions"),
                                style = TovTalkTheme.typography.bodyMedium,
                                color = TovTalkTheme.colors.textSecondary,
                                textAlign = TextAlign.Center
                            )

                            Spacer(modifier = Modifier.height(TovTalkTheme.spacing.lg))

                            // Phone number to call
                            Card(
                                colors = CardDefaults.cardColors(
                                    containerColor = TovTalkTheme.colors.brand.copy(alpha = TovTalkTheme.opacity.soft)
                                )
                            ) {
                                Column(
                                    modifier = Modifier.padding(TovTalkTheme.spacing.md),
                                    horizontalAlignment = Alignment.CenterHorizontally
                                ) {
                                    Text(
                                        text = StringProvider.get("verification_phone_to_call"),
                                        style = TovTalkTheme.typography.labelMedium,
                                        color = TovTalkTheme.colors.textSecondary
                                    )
                                    Spacer(modifier = Modifier.height(4.dp))
                                    Text(
                                        text = phoneToCall ?: "",
                                        style = TovTalkTheme.typography.headlineMedium,
                                        fontWeight = FontWeight.Bold,
                                        color = TovTalkTheme.colors.brand
                                    )
                                }
                            }

                            Spacer(modifier = Modifier.height(TovTalkTheme.spacing.md))

                            // Verification code
                            Card(
                                colors = CardDefaults.cardColors(
                                    containerColor = TovTalkTheme.colors.surfaceOverlay
                                )
                            ) {
                                Column(
                                    modifier = Modifier.padding(TovTalkTheme.spacing.md),
                                    horizontalAlignment = Alignment.CenterHorizontally
                                ) {
                                    Text(
                                        text = StringProvider.get("verification_code_label"),
                                        style = TovTalkTheme.typography.labelMedium,
                                        color = TovTalkTheme.colors.textSecondary
                                    )
                                    Spacer(modifier = Modifier.height(4.dp))
                                    Text(
                                        text = verificationCode ?: "",
                                        style = TovTalkTheme.typography.headlineLarge,
                                        fontWeight = FontWeight.Bold,
                                        color = TovTalkTheme.colors.textPrimary
                                    )
                                }
                            }

                            Spacer(modifier = Modifier.height(TovTalkTheme.spacing.lg))

                            Row(
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                CircularProgressIndicator(
                                    modifier = Modifier.size(16.dp),
                                    strokeWidth = 2.dp
                                )
                                Spacer(modifier = Modifier.padding(TovTalkTheme.spacing.sm))
                                Text(
                                    text = StringProvider.get("verification_waiting"),
                                    style = TovTalkTheme.typography.bodySmall,
                                    color = TovTalkTheme.colors.textSecondary
                                )
                            }

                            Spacer(modifier = Modifier.height(TovTalkTheme.spacing.md))

                            TextButton(onClick = { checkStatus() }) {
                                Text(StringProvider.get("verification_refresh"))
                            }
                        }
                    }
                }
                "approved" -> {
                    // Success
                    Card(
                        modifier = Modifier.fillMaxWidth(),
                        colors = CardDefaults.cardColors(
                            containerColor = TovTalkTheme.colors.success.copy(alpha = TovTalkTheme.opacity.soft)
                        ),
                        shape = TovTalkTheme.shapes.medium
                    ) {
                        Column(
                            modifier = Modifier.padding(TovTalkTheme.spacing.lg),
                            horizontalAlignment = Alignment.CenterHorizontally
                        ) {
                            Icon(
                                imageVector = Icons.Default.CheckCircle,
                                contentDescription = null,
                                tint = TovTalkTheme.colors.success,
                                modifier = Modifier.size(64.dp)
                            )

                            Spacer(modifier = Modifier.height(TovTalkTheme.spacing.md))

                            Text(
                                text = StringProvider.get("verification_success"),
                                style = TovTalkTheme.typography.titleLarge,
                                fontWeight = FontWeight.Bold,
                                color = TovTalkTheme.colors.success
                            )

                            Spacer(modifier = Modifier.height(TovTalkTheme.spacing.sm))

                            Text(
                                text = StringProvider.get("verification_success_desc"),
                                style = TovTalkTheme.typography.bodyMedium,
                                color = TovTalkTheme.colors.textSecondary,
                                textAlign = TextAlign.Center
                            )

                            Spacer(modifier = Modifier.height(TovTalkTheme.spacing.lg))

                            Button(
                                onClick = onNavigateBack,
                                modifier = Modifier.fillMaxWidth(),
                                colors = ButtonDefaults.buttonColors(
                                    containerColor = TovTalkTheme.colors.success
                                )
                            ) {
                                Text(StringProvider.get("common_done"))
                            }
                        }
                    }
                }
                "rejected", "expired" -> {
                    // Failed or expired
                    Card(
                        modifier = Modifier.fillMaxWidth(),
                        colors = CardDefaults.cardColors(
                            containerColor = TovTalkTheme.colors.error.copy(alpha = TovTalkTheme.opacity.soft)
                        ),
                        shape = TovTalkTheme.shapes.medium
                    ) {
                        Column(
                            modifier = Modifier.padding(TovTalkTheme.spacing.lg),
                            horizontalAlignment = Alignment.CenterHorizontally
                        ) {
                            Icon(
                                imageVector = Icons.Default.Error,
                                contentDescription = null,
                                tint = TovTalkTheme.colors.error,
                                modifier = Modifier.size(64.dp)
                            )

                            Spacer(modifier = Modifier.height(TovTalkTheme.spacing.md))

                            Text(
                                text = StringProvider.get("verification_failed"),
                                style = TovTalkTheme.typography.titleLarge,
                                fontWeight = FontWeight.Bold,
                                color = TovTalkTheme.colors.error
                            )

                            Spacer(modifier = Modifier.height(TovTalkTheme.spacing.sm))

                            Text(
                                text = StringProvider.get("verification_failed_desc"),
                                style = TovTalkTheme.typography.bodyMedium,
                                color = TovTalkTheme.colors.textSecondary,
                                textAlign = TextAlign.Center
                            )

                            Spacer(modifier = Modifier.height(TovTalkTheme.spacing.lg))

                            if (canStart) {
                                Button(
                                    onClick = {
                                        verificationStatus = null
                                        verificationCode = null
                                        phoneToCall = null
                                        errorMessage = null
                                    },
                                    modifier = Modifier.fillMaxWidth(),
                                    colors = ButtonDefaults.buttonColors(
                                        containerColor = TovTalkTheme.colors.brand
                                    )
                                ) {
                                    Text(StringProvider.get("verification_try_again"))
                                }
                            } else if (retryDaysRemaining != null) {
                                val days = retryDaysRemaining!!
                                Text(
                                    text = StringProvider.get("verification_retry_after_days").replaceDays(days) + " " +
                                            StringProvider.get("verification_retry_days_remaining").replaceDays(days),
                                    style = TovTalkTheme.typography.bodyMedium,
                                    color = TovTalkTheme.colors.textSecondary,
                                    textAlign = TextAlign.Center
                                )
                            }
                        }
                    }
                }
            }

            Spacer(modifier = Modifier.height(TovTalkTheme.spacing.xl))
        }
        }
        CenteredContentWrapper { phoneVerificationContent() }
    }
}
